import React, { useContext, useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// d.ts
import { DCRecord } from '@/@types/lib/dataController';

// Custom Components
import ModelTable from '@/ui/Table/ModelTable';
import useDocuments from '@/components/useDocuments';

// Ui
import TableButtonView from '@/ui/Table/ActionButtons/TableButtonView';
import TableButtonEdit from '@/ui/Table/ActionButtons/TableButtonEdit';
import { DialogFormGenerateFunction } from '@/ui/DataCentric/DataCentricPage';
import TableButtonDocuments from '@/ui/Table/ActionButtons/TableButtonDocuments';
import GeneralDialog from '@/ui/Dialog/GeneralDialog';
import TableButtonDelete from '@/ui/Table/ActionButtons/TableButtonDelete';

// Lib
import DataController, { IDataController } from '@/lib/DataController';

// Context
import DialogContext, { DialogContextType, IDialogProps } from '@/context/DialogContext/DialogContext';

// Types
import { IModel } from '@/@types/models/model';
import { ClosingDetails } from '@/@types/components/formController';
import { AllowRowActionFn, RowActionFn } from '@/@types/ui/Table';
import TableHeaderButtonShowDeleted from '../Table/HeaderButtons/TableHeaderButtonShowDeleted';

interface IDataCentricSecondaryTable {
  selectedPrimaryRecordId: number;
  selectedRecordId?: number;
  selectedPrimaryRecordQueryAttribute?: number | string | null;
  onRecordSelect: (id: number) => void;
  onLoad?: (records: Array<DCRecord>) => void;
  view: string | undefined;

  model: IModel;
  titleToken: string;

  baseRecordPath: string;
  childRecordRelativePath: string;

  allowView?: boolean;
  allowAdd?: boolean;
  allowEdit?: boolean;
  allowDelete?: boolean;
  allowFilter?: boolean;
  allowColumnPicker?: boolean;
  allowExport?: boolean;
  allowDocumentButton?: boolean;
  dialogComponent?: React.FunctionComponent<IDialogProps>;
  dialogForm?: string | DialogFormGenerateFunction;
  allowShowDeleted?: boolean;

  onRowAction?: RowActionFn;
  allowRowAction?: AllowRowActionFn;

  triggerRefresh: boolean;
  setTriggerRefresh: (state: boolean) => void;
  tableIdentifier?: string;
  foreignKeyValue?: DCRecord;

  setTriggerRefreshPrimary?: (state: boolean) => void;
}

const DataCentricSecondaryTable = (props: IDataCentricSecondaryTable) => {
  const { showDialog, showConfirmDialog } = useContext<DialogContextType>(DialogContext);
  const { checkIfHasGid, showDocumentsDialog } = useDocuments();

  const { t } = useTranslation();
  const { onRecordSelect, onLoad, onRowAction, allowRowAction } = props;
  const {
    model,
    titleToken,
    baseRecordPath,
    childRecordRelativePath,
    view,
    selectedPrimaryRecordId,
    selectedRecordId,
    triggerRefresh,
    setTriggerRefresh,
    selectedPrimaryRecordQueryAttribute,
    tableIdentifier,
    foreignKeyValue,
    allowColumnPicker,
    allowFilter,
    allowExport,
    allowDocumentButton = false,
    setTriggerRefreshPrimary,
    allowShowDeleted,
  } = props;
  const { allowView, allowAdd, allowEdit, allowDelete, dialogComponent = GeneralDialog, dialogForm } = props;

  const dc: IDataController = new DataController(model);

  const [records, setRecords] = useState<DCRecord[]>([]);

  const [showDeleted, setShowDeleted] = useState(false);

  const filteredRecords = useMemo(() => {
    if (!allowShowDeleted) return records;
    if (showDeleted) return records;
    return records.filter((rec) => !rec.is_deleted);
  }, [records, showDeleted]);

  const deletedHeaderButton = allowShowDeleted ? (
    <TableHeaderButtonShowDeleted showDeleted={showDeleted} onClick={() => setShowDeleted((prev) => !prev)} />
  ) : null;

  useEffect(() => {
    if (selectedPrimaryRecordId !== 0) {
      refreshRecords();
    } else {
      setRecords([]);
    }
  }, [selectedPrimaryRecordId, selectedPrimaryRecordQueryAttribute]);

  const refreshRecords = () => {
    if (selectedPrimaryRecordQueryAttribute) {
      fetchRecords(selectedPrimaryRecordQueryAttribute);
    } else if (selectedPrimaryRecordQueryAttribute === undefined && !selectedPrimaryRecordQueryAttribute && selectedPrimaryRecordId !== 0) {
      fetchRecords(selectedPrimaryRecordId);
    } else {
      setRecords([]);
    }
  };

  useEffect(() => {
    if (triggerRefresh) {
      setTriggerRefresh(false);
      refreshRecords();
    }
  }, [triggerRefresh]);

  const fetchRecords = (identifierField: number | string) => {
    const path = `${baseRecordPath}/${identifierField}/${childRecordRelativePath}`;
    dc.GetData(path)
      .then((resp) => {
        if (resp.success && Array.isArray(resp.data)) {
          setRecords(resp.data);
          if (onLoad) {
            onLoad(resp.data);
          }
        } else {
          setRecords([]);
        }
      })
      .catch(() => {
        setRecords([]);
      });
  };

  const handleView = (evt: React.SyntheticEvent, sel: { [key: string]: DCRecord }) => {
    const path = `${baseRecordPath}/${selectedPrimaryRecordId}/${childRecordRelativePath}`;
    const selId = parseInt(Object.keys(sel)[0], 10);
    const record: DCRecord = sel[selId];

    const form = typeof dialogForm === 'function' ? dialogForm(record) : dialogForm;

    if (dialogComponent && record?.id) {
      showDialog(dialogComponent, {
        dc,
        mode: 'view',
        form,
        recordId: record.id,
        customPath: path,
        onClose: handleDialogClose,
      });
    }
  };

  const handleEdit = (evt: React.SyntheticEvent, sel: { [key: string]: DCRecord }) => {
    const path = `${baseRecordPath}/${selectedPrimaryRecordId}/${childRecordRelativePath}`;
    const selId = parseInt(Object.keys(sel)[0], 10);
    const record: DCRecord = sel[selId];

    // console.log("record",record);

    const form = typeof dialogForm === 'function' ? dialogForm(record) : dialogForm;

    if (dialogComponent && record?.id) {
      showDialog(dialogComponent, {
        dc,
        mode: 'update',
        form,
        initialRecord: record,
        onClose: handleDialogClose,
        customPath: path,
        title: `${t(titleToken)} - ${t('common.update')}`,
      });
    }
  };

  const handleAdd = () => {
    const path = `${baseRecordPath}/${selectedPrimaryRecordId}/${childRecordRelativePath}`;

    if (dialogComponent) {
      showDialog(dialogComponent, {
        dc,

        mode: 'insert',
        form: 'insert',
        onClose: handleDialogClose,
        customPath: path,
        title: `${t(titleToken)} - ${t('common.insert')}`,
        initialRecord: foreignKeyValue,
      });
    }
  };

  const handleDelete = async (evt: React.SyntheticEvent, sel: { [key: string]: DCRecord }) => {
    const record = sel[Object.keys(sel)[0]];
    const confirmResult = await showConfirmDialog({
      title: t(titleToken) as string,
      text: `${t('cdialogs.are_you_sure')}`,
      confirmButtonText: t('buttons.confirm') as string,
      cancelButtonText: t('buttons.cancel') as string,
    });

    if (confirmResult.success && record.id) {
      await dc
        .DeleteRecord(record.id as number)
        .finally(() => {
          refreshRecords();
        });
    }
  };

  const handleDialogClose = (result: ClosingDetails) => {
    if (result.dataChanged) {
      refreshRecords();
      if (setTriggerRefreshPrimary) {
        setTriggerRefreshPrimary(true);
      }
    }
  };

  const handleDocuments = (evt: React.SyntheticEvent, sel: { [key: string]: DCRecord }) => {
    const selId = parseInt(Object.keys(sel)[0], 10);
    const record: DCRecord = sel[selId];
    const gid = record.gid as string;
  
    if (gid) {
      showDocumentsDialog(gid, handleDialogClose);
    }
  };
  
  const isDocumentsButtonEnabled: (selection: { [key: string]: DCRecord }) => boolean = (sel) => {
    const selId = parseInt(Object.keys(sel)[0], 10);
    const record: DCRecord = sel[selId];
  
    if (allowShowDeleted) {
      return !!record.gid && !record.is_deleted;
    }
  
    return !!record.gid;
  };

  const hasGid = checkIfHasGid(records);

  return (
    <ModelTable
      identifier={tableIdentifier}
      title={t(titleToken)}
      records={filteredRecords}
      dc={dc}
      allowSelection="one"
      allowFilter={allowFilter}
      allowColumnPicker={allowColumnPicker}
      allowExport={allowExport}
      allowAdd={allowAdd}
      handleAdd={handleAdd}
      onRowAction={onRowAction !== undefined ? onRowAction : () => {}}
      allowRowAction={allowRowAction || (() => true)}
      disableAdd={!selectedPrimaryRecordId}
      onRowClick={(evt: React.SyntheticEvent, sel: { [key: string]: DCRecord }) => {
        const clickedRecord = sel[Object.keys(sel)[0]];
        return onRecordSelect(clickedRecord.id as number);
      }}
      viewName={view}
      rowSelectedCustom={selectedRecordId ?? selectedPrimaryRecordId}
      customHeaderControl={deletedHeaderButton}
    >
      {/* <TableButtonShowPhotos
        disabled={!imagesEnabled}
        variant="contained"
        onClick={setAllowImages}
      /> */}
      {allowDocumentButton && hasGid ? <TableButtonDocuments isEnabledRecord={isDocumentsButtonEnabled} onClick={handleDocuments} /> : null}
      {allowDelete ? <TableButtonDelete variant="outlined" onClick={handleDelete} /> : null}
      {allowEdit ? <TableButtonEdit variant="outlined" onClick={handleEdit} /> : null}
      {allowView ? <TableButtonView variant="contained" onClick={handleView} /> : null}
    </ModelTable>
  );
};

export default DataCentricSecondaryTable;
